import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ArrowRight, Check } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import { Container, Reveal, ImageReveal } from "@/components/Reveal";
import ServiceIcon from "@/components/ServiceIcon";
import { ServiceBadge } from "@/components/ServiceBadge";
import ContactForm from "@/components/ContactForm";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  getState,
  availableServices,
  shortLabels,
  industryLink,
  SERVICE_META,
} from "@/data/serviceAreas";
import { getArticle } from "@/data/education";
import { STATE_NAMES, COMPANY } from "@/data/content";

const SERVICE_DESC = {
  electricity: "Competitive electricity procurement, contract strategy, and price-risk management for commercial loads.",
  gas: "Strategic natural gas supply procurement and seasonal hedging for facilities and multi-site operators.",
  battery: "Host battery energy storage on your property as part of our growing Virtual Power Plant Network.",
};

export default function StateDetail() {
  const { stateId } = useParams();
  const slug = (stateId || "").toLowerCase();
  const st = getState(slug);

  // Unsupported but valid US state -> contact prompt
  if (!st) {
    const name = STATE_NAMES[slug];
    if (!name) return <Navigate to="/service-areas" replace />;
    return (
      <>
        <SEO
          title={`Commercial Energy Services in ${name}`}
          description={`Check commercial energy service availability from Innervation Energy Group in ${name}.`}
          path={`/service-areas/${slug}`}
        />
        <PageHero
          overline="Service Areas"
          titleLines={["Commercial Energy", `Services in ${name}`]}
          subtitle={`We may still be able to help businesses in ${name}. Market access changes over time — contact us to check current availability.`}
          crumbs={[
            { label: "Service Areas", path: "/service-areas" },
            { label: name, path: `/service-areas/${slug}` },
          ]}
        />
        <section className="bg-white py-24 md:py-28">
          <Container>
            <Link
              to="/service-areas"
              className="mb-12 inline-flex items-center gap-2 text-sm font-medium text-slatey transition-colors hover:text-navy"
            >
              <ArrowLeft className="h-4 w-4" /> All service areas
            </Link>
            <div className="border border-line bg-ice p-10 md:p-14">
              <h2 className="font-heading text-2xl font-semibold text-navy md:text-3xl">
                Let's check availability in {name}
              </h2>
              <p className="mt-4 max-w-xl leading-relaxed text-slatey">
                Reach out and we'll confirm what's currently available for commercial
                organizations in {name}.
              </p>
              <Link
                to="/contact"
                className="mt-8 inline-flex items-center gap-2 bg-electric px-7 py-4 text-sm font-medium text-white transition-transform hover:scale-[0.97] hover:bg-electric-hover"
              >
                Contact Us <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Container>
        </section>
      </>
    );
  }

  const services = availableServices(st);
  const primaryService = SERVICE_META[services[0].key].label;
  const relatedArticles = st.relatedArticleSlugs.map(getArticle).filter(Boolean);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: st.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <SEO
        title={`Commercial Energy Services in ${st.name}`}
        description={st.intro}
        path={`/service-areas/${slug}`}
        jsonLd={jsonLd}
      />
      <PageHero
        overline={`Service Areas · ${st.abbr}${st.grid ? ` · ${st.grid}` : ""}`}
        titleLines={["Commercial Energy", `Services in ${st.name}`]}
        subtitle={st.intro}
        crumbs={[
          { label: "Service Areas", path: "/service-areas" },
          { label: st.name, path: `/service-areas/${slug}` },
        ]}
      >
        <div className="flex flex-wrap gap-2">
          {shortLabels(st).map((l, i) => (
            <ServiceBadge key={i} serviceKey={services[i].key} />
          ))}
        </div>
      </PageHero>

      {/* Available services */}
      <section className="bg-white py-24 md:py-28">
        <Container>
          <Link
            to="/service-areas"
            className="mb-12 inline-flex items-center gap-2 text-sm font-medium text-slatey transition-colors hover:text-navy"
          >
            <ArrowLeft className="h-4 w-4" /> All service areas
          </Link>

          <p className="overline text-electric">Available in {st.name}</p>
          <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-navy md:text-4xl">
            {services.length} service{services.length > 1 ? "s" : ""} available.
          </h2>

          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {services.map((s) => {
              const m = SERVICE_META[s.key];
              return (
                <Reveal key={s.key}>
                  <Link
                    to={m.solution}
                    data-testid={`state-service-${s.key}`}
                    className="group flex h-full flex-col border border-line p-8 transition-colors hover:border-electric"
                  >
                    <div
                      className="flex h-12 w-12 items-center justify-center rounded-sm"
                      style={{ backgroundColor: `${m.color}14`, color: m.color }}
                    >
                      <ServiceIcon name={m.icon} className="h-6 w-6" />
                    </div>
                    <h3 className="mt-6 flex items-center gap-2 font-heading text-xl font-medium text-navy">
                      <Check className="h-4 w-4 text-energy" />
                      {m.label}
                    </h3>
                    <p className="mt-3 flex-1 leading-relaxed text-slatey">
                      {SERVICE_DESC[s.key]}
                    </p>
                    {(s.note || s.requirement) && (
                      <p className="mt-4 border-t border-line pt-4 font-mono text-[11px] uppercase leading-relaxed tracking-[0.08em] text-slatey">
                        {s.note}
                        {s.requirement ? ` — ${s.requirement}` : ""}
                      </p>
                    )}
                    <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-electric">
                      Learn more
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Understanding the market */}
      <section className="bg-ice py-24 md:py-28">
        <Container>
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <p className="overline text-electric">Understanding the Market</p>
                <h2 className="mt-4 font-heading text-3xl font-semibold leading-[1.12] tracking-tight text-navy md:text-4xl">
                  The {st.name} commercial energy market.
                </h2>
                <p className="mt-5 leading-relaxed text-slatey">
                  A plain-language look at how commercial energy works in {st.name} —
                  written to educate, not to sell.
                </p>
              </div>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <div className="space-y-10">
                {st.market.map((sec, i) => (
                  <Reveal key={i} delay={0.04}>
                    <div>
                      <h3 className="font-heading text-xl font-medium text-navy md:text-2xl">
                        {sec.heading}
                      </h3>
                      <p className="mt-3 text-lg leading-relaxed text-navy/75">
                        {sec.body}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Industries served */}
      <section className="bg-white py-24 md:py-28">
        <Container>
          <p className="overline text-electric">Industries Served</p>
          <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-navy md:text-4xl">
            Commonly supported in {st.name}.
          </h2>
          <div className="mt-10 flex flex-wrap gap-3">
            {st.industries.map((label) => (
              <Link
                key={label}
                to={industryLink(label)}
                data-testid={`state-industry-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group inline-flex items-center gap-2 border border-line px-5 py-3 text-sm font-medium text-navy transition-colors hover:border-electric hover:text-electric"
              >
                {label}
                <ArrowUpRight className="h-4 w-4 text-slatey transition-colors group-hover:text-electric" />
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* Related services */}
      <section className="bg-navy-dark py-20 text-white md:py-24">
        <Container>
          <p className="overline text-energy">Related Services</p>
          <h2 className="mt-4 font-heading text-2xl font-semibold tracking-tight md:text-3xl">
            Explore the solutions available in {st.name}.
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {services.map((s) => {
              const m = SERVICE_META[s.key];
              return (
                <Link
                  key={s.key}
                  to={m.solution}
                  className="group flex items-center justify-between border border-white/10 bg-navy p-7 transition-colors hover:border-white/30"
                >
                  <div className="flex items-center gap-4">
                    <span
                      className="flex h-11 w-11 items-center justify-center rounded-sm"
                      style={{ backgroundColor: `${m.color}22`, color: m.color }}
                    >
                      <ServiceIcon name={m.icon} className="h-5 w-5" />
                    </span>
                    <span className="font-heading text-lg font-medium">{m.label}</span>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-white/50 transition-colors group-hover:text-white" />
                </Link>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Related education articles */}
      <section className="bg-ice py-24 md:py-28">
        <Container>
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="overline text-electric">Education Center</p>
              <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight text-navy md:text-4xl">
                Related reading for {st.name}.
              </h2>
            </div>
            <Link
              to="/education"
              className="group inline-flex items-center gap-2 text-sm font-medium text-navy"
            >
              Explore the library
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {relatedArticles.map((a) => (
              <Link
                key={a.slug}
                to={`/education/${a.slug}`}
                data-testid={`state-article-${a.slug}`}
                className="group flex h-full flex-col overflow-hidden border border-line bg-white transition-colors hover:border-electric"
              >
                <ImageReveal src={a.image} alt={a.title} className="aspect-[16/10]" />
                <div className="flex flex-1 flex-col p-5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-electric">
                    {a.categoryLabel}
                  </span>
                  <h3 className="mt-2 flex-1 font-heading text-base font-medium leading-snug text-navy transition-colors group-hover:text-electric">
                    {a.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="bg-white py-24 md:py-28">
        <Container>
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-4">
              <p className="overline text-electric">FAQ</p>
              <h2 className="mt-4 font-heading text-3xl font-semibold leading-[1.12] tracking-tight text-navy md:text-4xl">
                {st.name} questions, answered.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <Accordion type="single" collapsible className="border-t border-line">
                {st.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-line">
                    <AccordionTrigger className="py-5 text-left text-base text-navy hover:no-underline">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-slatey">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </Container>
      </section>

      {/* Contact */}
      <section className="bg-ice py-24 md:py-28">
        <Container>
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-2 lg:gap-20">
            <div>
              <p className="overline text-electric">Contact</p>
              <h2 className="mt-4 font-heading text-3xl font-semibold leading-[1.12] tracking-tight text-navy md:text-4xl">
                Doing business in {st.name}?
              </h2>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-slatey">
                Tell us about your facilities in {st.name} and what you'd like help with.
                A commercial energy advisor will follow up shortly.
              </p>
              <a
                href={`mailto:${COMPANY.email}`}
                className="mt-8 inline-block font-medium text-electric hover:underline"
              >
                {COMPANY.email}
              </a>
            </div>
            <Reveal delay={0.1}>
              <div className="border border-line bg-white p-8 md:p-10">
                <ContactForm defaultState={STATE_NAMES[slug] || st.name} defaultService={primaryService} />
              </div>
            </Reveal>
          </div>
        </Container>
      </section>
    </>
  );
}
